import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, fmtINR, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Btn, Field, Input, Select, Empty } from "@/components/UI";
import { toast } from "sonner";

const empty = {
  date: today(), supplier_id: "", yarn_type: "", kg: "", rate: "", lot_no: "",
  send_to_knitter_id: "", send_kg: "", notes: "",
};

export default function YarnPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [knitters, setKnitters] = useState([]);
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [sendFor, setSendFor] = useState(null); // yarn row selected for "Send to Knitter"

  const load = () => api.get("/yarn").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/parties").then((r) => {
      setSuppliers(r.data.filter((p) => p.type === "yarn_supplier"));
      setKnitters(r.data.filter((p) => p.type === "knitter"));
    });
  }, []);

  const startEdit = (y) => {
    setEditing(y.id);
    setF({
      date: y.date, supplier_id: y.supplier_id, yarn_type: y.yarn_type,
      kg: y.kg, rate: y.rate, lot_no: y.lot_no || "",
      send_to_knitter_id: "", send_kg: "", notes: y.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setF(empty); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, kg: Number(f.kg), rate: Number(f.rate) };
      // Only include send fields on create (backend ignores them on PUT anyway)
      if (editing) {
        delete payload.send_to_knitter_id;
        delete payload.send_kg;
      } else {
        payload.send_kg = Number(f.send_kg || 0);
      }
      if (editing) {
        await api.put(`/yarn/${editing}`, payload);
        toast.success("Yarn purchase updated · ledger adjusted");
      } else {
        const { data } = await api.post("/yarn", payload);
        if (data.knitting_dc_number) {
          toast.success(`Yarn recorded · Knitting DC ${data.knitting_dc_number} auto-created`);
        } else {
          toast.success("Yarn recorded · kept in stock");
        }
      }
      cancelEdit();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this yarn purchase? Linked ledger bill will also be removed.")) return;
    await api.delete(`/yarn/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader label="Stage 01" title="Yarn Purchase" subtitle="Track yarn intake — send to knitter now or keep as stock" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={editing ? "Edit Purchase" : "New Purchase"} right={editing && (
          <button type="button" onClick={cancelEdit} className="label-caps hover:text-destructive" data-testid="yarn-cancel">Cancel</button>
        )}>
          <form onSubmit={submit} className="space-y-3" data-testid="yarn-form">
            <Field label="Date"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
            <Field label="Supplier">
              <Select data-testid="yarn-supplier" required value={f.supplier_id} onChange={(e) => setF({ ...f, supplier_id: e.target.value })}>
                <option value="">Select supplier…</option>
                {suppliers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
            <Field label="Yarn Type"><Input data-testid="yarn-type" required value={f.yarn_type} onChange={(e) => setF({ ...f, yarn_type: e.target.value })} placeholder="Cotton 30s / Polyester …" /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Quantity (Kg)"><Input data-testid="yarn-kg" type="number" step="0.01" required value={f.kg} onChange={(e) => setF({ ...f, kg: e.target.value })} /></Field>
              <Field label="Rate (₹ / Kg)"><Input data-testid="yarn-rate" type="number" step="0.01" required value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} /></Field>
            </div>
            <Field label="Lot # (auto if blank)">
              <Input data-testid="yarn-lot" value={f.lot_no} onChange={(e) => setF({ ...f, lot_no: e.target.value.toUpperCase() })} placeholder="LOT-30S-A12" />
            </Field>
            <div className="border border-border p-2 bg-secondary tabular text-sm">
              Amount: <b>{fmtINR((Number(f.kg) || 0) * (Number(f.rate) || 0))}</b>
            </div>

            {!editing && (
              <div className="border border-dashed border-border p-3 bg-white space-y-2" data-testid="yarn-send-to-knitter-block">
                <div className="label-caps">Send to Knitter (optional)</div>
                <div className="text-xs text-muted-foreground">Auto-creates a Knitting DC. Leave blank to keep in stock.</div>
                <Field label="Knitter">
                  <Select data-testid="yarn-send-knitter" value={f.send_to_knitter_id} onChange={(e) => setF({ ...f, send_to_knitter_id: e.target.value, send_kg: e.target.value ? (f.send_kg || f.kg) : "" })}>
                    <option value="">— keep as stock —</option>
                    {knitters.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                  </Select>
                </Field>
                {f.send_to_knitter_id && (
                  <Field label={`Kg to send (of ${f.kg || 0} kg purchased)`}>
                    <Input data-testid="yarn-send-kg" type="number" step="0.01" value={f.send_kg} onChange={(e) => setF({ ...f, send_kg: e.target.value })} />
                  </Field>
                )}
              </div>
            )}

            <Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
            <Btn data-testid="yarn-submit">{editing ? "Save Changes" : "Record Purchase"}</Btn>
          </form>
        </Card>

        <Card title="Purchase History" className="lg:col-span-2">
          <div className="table-scroll">
            <table className="table-dense w-full">
              <thead><tr><th>Date</th><th>Supplier</th><th>Type</th><th>Lot #</th><th className="text-right">Kg</th><th className="text-right">Sent / Stock</th>{isAdmin && <><th className="text-right">Rate</th><th className="text-right">Amount</th></>}<th></th></tr></thead>
              <tbody>
                {list.map((y) => {
                  const sent = Number(y.kg_sent_to_knitting || 0);
                  const stock = Math.max(0, Number(y.kg) - sent);
                  return (
                    <tr key={y.id} className={editing === y.id ? "bg-secondary" : ""}>
                      <td className="font-mono">{y.date}</td>
                      <td>{y.supplier_name}</td>
                      <td>{y.yarn_type}</td>
                      <td className="font-mono text-xs">
                        <div>{y.lot_no ? (
                          <Link to={`/lots/${encodeURIComponent(y.lot_no)}`} className="hover:underline text-accent" data-testid={`yarn-lot-link-${y.id}`}>{y.lot_no}</Link>
                        ) : <span className="text-muted-foreground">—</span>}</div>
                        {y.knitting_dc_number && <div className="text-accent text-[10px]">{y.knitting_dc_number}</div>}
                      </td>
                      <td className="tabular text-right">{y.kg}</td>
                      <td className="tabular text-right text-xs">
                        <div>{sent} sent</div>
                        <div className="text-muted-foreground">{stock} stock</div>
                      </td>
                      {isAdmin && <td className="tabular text-right">₹{y.rate}</td>}
                      {isAdmin && <td className="tabular text-right font-bold">{fmtINR(y.amount)}</td>}
                      <td className="text-right whitespace-nowrap">
                        {stock > 0 && (
                          <button
                            onClick={() => setSendFor(y)}
                            className="label-caps text-accent hover:underline mr-3"
                            data-testid={`yarn-send-${y.id}`}
                          >
                            → Knit
                          </button>
                        )}
                        {isAdmin && (
                          <>
                            <button onClick={() => startEdit(y)} className="label-caps hover:text-foreground mr-3" data-testid={`yarn-edit-${y.id}`}>Edit</button>
                            <button onClick={() => del(y.id)} className="label-caps hover:text-destructive">Del</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {list.length === 0 && <tr><td colSpan={isAdmin ? 9 : 7}><Empty text="No yarn purchases yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      {sendFor && (
        <SendToKnitterModal
          yarn={sendFor}
          knitters={knitters}
          onClose={() => setSendFor(null)}
          onSaved={() => { setSendFor(null); load(); }}
        />
      )}
    </div>
  );
}

function SendToKnitterModal({ yarn, knitters, onClose, onSaved }) {
  const remaining = Math.max(0, Number(yarn.kg) - Number(yarn.kg_sent_to_knitting || 0));
  const [f, setF] = useState({
    knitter_id: "", kg: String(remaining), rate: "", lot_no: yarn.lot_no || "",
    date: today(), notes: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.knitter_id) return toast.error("Choose a knitter");
    try {
      const { data } = await api.post(`/yarn/${yarn.id}/send-to-knitter`, {
        knitter_id: f.knitter_id, kg: Number(f.kg), rate: Number(f.rate || 0),
        lot_no: f.lot_no, date: f.date, notes: f.notes,
      });
      toast.success(`Knitting DC ${data.dc_number} auto-created`);
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-background border-2 border-border w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="yarn-send-modal">
        <div>
          <div className="label-caps">Send to Knitter</div>
          <div className="font-display font-black text-xl mt-1">{yarn.yarn_type}</div>
          <div className="text-xs font-mono text-muted-foreground mt-1">Lot {yarn.lot_no || "—"} · {remaining} kg available</div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Knitter">
            <Select required value={f.knitter_id} onChange={(e) => setF({ ...f, knitter_id: e.target.value })} data-testid="send-knitter-select">
              <option value="">Select knitter…</option>
              {knitters.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label={`Kg to send (max ${remaining})`}>
              <Input type="number" step="0.01" required max={remaining} value={f.kg} onChange={(e) => setF({ ...f, kg: e.target.value })} data-testid="send-kg" />
            </Field>
            <Field label="Rate ₹/kg">
              <Input type="number" step="0.01" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="DC Date"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
            <Field label="Lot # (propagates)"><Input value={f.lot_no} onChange={(e) => setF({ ...f, lot_no: e.target.value.toUpperCase() })} /></Field>
          </div>
          <Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 h-10 label-caps border border-border hover:bg-secondary">Cancel</button>
            <Btn data-testid="send-submit">Create Knitting DC</Btn>
          </div>
        </form>
      </div>
    </div>
  );
}
